// Client-side incident logging: buffered, fire-and-forget.
import { logIncidentFn } from "./incident-log.functions";

type Severity = "info" | "warn" | "error" | "critical";
type Source = "client" | "auth" | "business";

export type ClientIncident = {
  severity?: Severity;
  source?: Source;
  event_type: string;
  message: string;
  metadata?: Record<string, unknown>;
};

const recent = new Map<string, number>();
const DEDUPE_MS = 10_000;

function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  recent.set(key, now);
  if (recent.size > 200) {
    for (const [k, t] of recent) if (now - t > DEDUPE_MS) recent.delete(k);
  }
  return true;
}

export function logIncident(incident: ClientIncident): void {
  if (typeof window === "undefined") return;
  const key = `${incident.source ?? "client"}|${incident.event_type}|${incident.message.slice(0, 120)}`;
  if (!shouldSend(key)) return;

  const route = window.location?.pathname ?? null;
  const payload = {
    severity: incident.severity ?? "error",
    source: incident.source ?? "client",
    event_type: incident.event_type,
    message: incident.message,
    route,
    metadata: incident.metadata ?? {},
  };
  // fire-and-forget
  logIncidentFn({ data: payload }).catch(() => {
    /* ignore */
  });
}

function stringifyError(err: unknown): string {
  if (err instanceof Error)
    return `${err.name}: ${err.message}${err.stack ? "\n" + err.stack : ""}`;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

let installed = false;
export function installClientIncidentHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    logIncident({
      severity: "error",
      source: "client",
      event_type: "window_error",
      message: stringifyError(event.error ?? event.message),
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    logIncident({
      severity: "error",
      source: "client",
      event_type: "unhandled_rejection",
      message: stringifyError(event.reason),
    });
  });
}
